// components/Planet.jsx - 行星组件（代表职业经历）
import React, { useState, useRef, Suspense } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, useTexture } from '@react-three/drei';
// Satellite组件已移除，全部整合到Planet组件中
import * as THREE from 'three'; // 添加THREE导入

// 辅助组件，仅在提供有效纹理源时加载和应用纹理
function TexturedPlanetMaterial({ textureSrc, isRelevant, isHovered }) {
  // 打印纹理源信息，便于调试
  console.log(`尝试加载纹理 (TexturedPlanetMaterial): ${textureSrc}`);

  // Hooks必须在所有条件返回语句之前调用
  const [textureError, setTextureError] = useState(false);
  // 即使textureSrc可能无效，也先调用useTexture，让其内部处理或依赖后续检查
  // 如果textureSrc为null/undefined，useTexture可能会抛错或返回特定值，需测试
  // 或者，如果textureSrc确定无效，可以给useTexture传递一个已知的最小有效路径或null
  const safeTextureSrc = textureSrc || ''; // 提供一个空字符串以避免useTexture直接因null/undefined崩溃

  const textureData = useTexture(safeTextureSrc, undefined, (error) => {
    if (safeTextureSrc) { // 只在尝试加载有效src时记录错误
      console.error(`加载纹理失败: ${safeTextureSrc}`, error);
      setTextureError(true);
    }
  });

  if (!textureSrc || !safeTextureSrc) { // 检查原始textureSrc和安全版本
    console.error("TexturedPlanetMaterial 收到无效 textureSrc 或 safeTextureSrc 为空，回退到meshBasicMaterial。");
    return <meshBasicMaterial color="#666666" side={THREE.DoubleSide} />;
  }
  
  if (textureError) {
    console.warn(`纹理 ${textureSrc} 加载失败，回退到meshBasicMaterial。`);
    return <meshBasicMaterial color="#888888" side={THREE.DoubleSide} />;
  }

  // 正常加载纹理
  const emissiveHighlightColor = '#FFFFFF';
  const emissiveColor = (isRelevant && isHovered) ? emissiveHighlightColor : (isRelevant ? '#555555' : '#000000');
  const emissiveIntensity = (isRelevant && isHovered) ? 0.4 : (isRelevant ? 0.15 : 0);
  const materialOpacity = isRelevant ? 1 : 0.7;

  return (
    <meshStandardMaterial
      map={textureData}
      metalness={0.3}
      roughness={0.7}
      emissive={emissiveColor}
      emissiveIntensity={emissiveIntensity}
      opacity={materialOpacity}
      transparent={materialOpacity < 1}
      side={THREE.DoubleSide}
    />
  );
}

export function Planet({ 
  orbitRadius,
  orbitAngle, // 新参数：轨道角度，取代position和initialAngle
  orbitSpeed, 
  rotationSpeed, 
  size, // 继续支持直接传入size，便于统一系统中使用
  sizeCategory = 2, // 继续支持直接传入sizeCategory
  texture, // 支持直接传入texture
  color = "#3498db", // 支持直接传入color
  label, 
  experience, // 包含 { id, title, importance, textureUrl, tags, content, ... }
  onClick,    
  onSatelliteClick, 
  projects = [],
  activeSkillFilter,
  selectedExperience = null,
  selectedProject = null
}) {
  const planetRef = useRef(); // 行星网格引用
  const orbitRef = useRef(); // 轨道缓存引用
  const [hovered, setHovered] = useState(false);
  // 累计公转时间，初始设置为传入的轨道角度，确保行星从不同初始位置开始公转
  const orbitTimeRef = useRef(orbitAngle);
  
  // 从 experience 对象获取 textureUrl，或使用直接传入的texture
  const textureUrl = texture || (experience && experience.textureUrl);
  console.log("Rendering planet:", label, "with textureUrl:", textureUrl);

  // 判断行星或其卫星是否与当前技能筛选匹配
  const planetMatchesSkill = activeSkillFilter && experience && experience.skills && experience.skills.includes(activeSkillFilter);
  const anySatelliteMatchesSkill = activeSkillFilter && projects.some(p => p.technologies && p.technologies.includes(activeSkillFilter));
  const isRelevant = !activeSkillFilter || planetMatchesSkill || anySatelliteMatchesSkill;

  // 同时处理行星的自转和公转
  useFrame((_, delta) => {
    // 调整速度基于相关性和悬停状态
    let currentRotationSpeed = rotationSpeed;
    let currentOrbitSpeed = orbitSpeed;
    
    if (!isRelevant && !hovered) { 
      currentRotationSpeed *= 0.5;
      currentOrbitSpeed *= 0.5;
    } else if (hovered && isRelevant) { 
      currentRotationSpeed *= 0.8;
      currentOrbitSpeed *= 0.8;
    }
    
    // 更新行星自转
    if (planetRef.current && currentRotationSpeed > 0) {
      planetRef.current.rotation.y += currentRotationSpeed * 0.01;
    }
    
    // 更新行星公转位置
    if (orbitRef.current && currentOrbitSpeed > 0) {
      // 累加公转时间，保持各行星的相对位置关系
      orbitTimeRef.current += delta * currentOrbitSpeed;
      
      // 基于累计时间计算新位置
      const x = Math.cos(orbitTimeRef.current) * orbitRadius;
      const z = Math.sin(orbitTimeRef.current) * orbitRadius;
      orbitRef.current.position.set(x, 0, z);
    }
  });
  
  // 计算职业时长
  const getDuration = () => {
    if (!experience || !experience.startDate) return ""; // 添加检查以防 experience 未定义
    const start = new Date(experience.startDate);
    const end = experience.endDate ? new Date(experience.endDate) : new Date();
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    return years > 0 
      ? `${years}年${remainingMonths > 0 ? remainingMonths + '个月' : ''}`
      : `${remainingMonths}个月`;
  };
  
  // 根据传入的size、sizeCategory或experience.importance调整实际大小
  let actualSize;
  
  // 优先使用直接传入的size
  if (size !== undefined) {
    actualSize = size;
  } else if (experience && experience.importance !== undefined) {
    // 否则根据experience.importance确定大小
    if (experience.importance === 1) {
      actualSize = 2.0; // 小
    } else if (experience.importance === 3) {
      actualSize = 7.0; // 大
    } else {
      actualSize = 4.0; // 默认中等大小
    }
  } else {
    // 如果没有experience或importance，则根据sizeCategory确定
    if (sizeCategory === 1) {
      actualSize = 2.0; // 小
    } else if (sizeCategory === 3) {
      actualSize = 7.0; // 大
    } else {
      actualSize = 4.0; // 默认中等大小
    }
  }
  
  const labelYOffset = actualSize * 1.8; // 根据实际大小计算标签偏移，增加此值以提高标签位置

  // 轨道材质属性 (使用中性色或基于相关性)
  const ringColor = isRelevant ? '#666666' : '#444444';
  const ringOpacity = isRelevant ? 0.2 : 0.05;
  
  return (
    <group visible={isRelevant || hovered}> {/* 如果不相关且未悬停，则行星组不可见，除非悬停 */} 
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <ringGeometry args={[orbitRadius - 0.1, orbitRadius + 0.1, 128]} />
        <meshBasicMaterial color={ringColor} transparent opacity={ringOpacity} />
      </mesh>
      
      {/* 行星初始位置 - 基于轨道半径和角度计算，后续由useFrame更新 */}
      <group ref={orbitRef} position={[
        Math.cos(orbitAngle) * orbitRadius, // 初始 x坐标 = 半径 * cos(角度)
        0,                                 // y坐标 = 0（水平面）
        Math.sin(orbitAngle) * orbitRadius  // 初始 z坐标 = 半径 * sin(角度)
      ]}>
        <mesh
          ref={planetRef}
          onPointerOver={(e) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer';}}
          onPointerOut={(e) => { e.stopPropagation(); setHovered(false); document.body.style.cursor = 'auto'; }}
          onClick={(e) => { e.stopPropagation(); if(isRelevant) onClick(experience); }}
          scale={hovered && isRelevant ? [1.1, 1.1, 1.1] : [1, 1, 1]}
          castShadow // 添加投影支持
          receiveShadow // 添加接收阴影支持
        >
          <sphereGeometry args={[actualSize, 64, 64]} /> {/* 增加细分度使表面更光滑 */}
          {/* 如果有纹理则使用纹理，否则使用强化的材质 */}
          {textureUrl ? (
            <Suspense fallback={<meshStandardMaterial color={color || "#444444"} roughness={0.7} metalness={0.2} />}>
              <TexturedPlanetMaterial textureSrc={textureUrl} isRelevant={isRelevant} isHovered={hovered} />
            </Suspense>
          ) : (
            <meshStandardMaterial 
              color={color} 
              metalness={0.25} // 调整金属度以增强高光效果
              roughness={0.65} // 缩小粗糙度增强光滑度
              emissive={isRelevant && hovered ? '#FFFFFF' : (isRelevant ? '#555555' : '#000000')}
              emissiveIntensity={isRelevant && hovered ? 0.6 : (isRelevant ? 0.25 : 0)} // 增强发光强度
              opacity={isRelevant ? 1 : 0.7}
              transparent={!isRelevant}
              envMapIntensity={1.2} // 增强环境反射
              clearcoat={0.3} // 添加清漆效果
              clearcoatRoughness={0.3} // 清漆粗糙度
            />
          )}
        </mesh>
        
        {/* 行星标签 - 默认完全隐藏，仅在以下情况显示:
           1. 悬停在行星上
           2. 行星被选中
           3. 技能标签被选中，且行星具有该技能
        */}
        {(hovered || 
          (selectedExperience && experience && experience.id === selectedExperience.id) || 
          (selectedProject && experience && experience.id === selectedProject.id) ||
          // 新增条件：有技能标签被选中且当前行星具有该技能
          (activeSkillFilter && (
            // 经历行星判断是否具有该技能
            (experience && experience.skills && experience.skills.includes(activeSkillFilter)) ||
            // 项目行星判断是否具有该技能
            (experience && experience.technologies && experience.technologies.includes(activeSkillFilter))
          ))) && (
          <Html 
            distanceFactor={55} 
            position={[0, labelYOffset, 0]} 
            center
            zIndexRange={[0, 5]} // 设置z-index范围，确保不高于UI组件
            occlude // 启用遮挡功能，被其他3D物体遮挡时会隐藏
          >
            <div className={`planet-label ${hovered && isRelevant ? 'hovered' : ''} ${!isRelevant ? 'dimmed' : ''}`}>
              <h3>{label}</h3>
              {experience && experience.title && <p>{experience.title}</p>}
              {experience && experience.startDate && <p>{getDuration()}</p>}
            </div>
          </Html>
        )}
        
        {/* 项目卫星 - 直接使用Planet组件渲染 */}
        {projects.map((project, index) => {
          // 围绕行星的卫星轨道
          const satelliteAngle = (index / projects.length) * Math.PI * 2;
          const satelliteDistance = Math.max(actualSize * 1.8, 2.5); // 使用 actualSize
          const sx = Math.cos(satelliteAngle) * satelliteDistance;
          const sz = Math.sin(satelliteAngle) * satelliteDistance;
          
          // 判断卫星是否与当前技能筛选匹配
          const satelliteMatchesSkill = activeSkillFilter && project.technologies && project.technologies.includes(activeSkillFilter);
          const satelliteIsRelevant = !activeSkillFilter || satelliteMatchesSkill || isRelevant;
          const satelliteSize = Math.max(actualSize * 0.20, 0.3); // 使用 actualSize，调整卫星大小，确保最小尺寸
          
          return (
            <group key={project.id} position={[sx, 0, sz]}>
              <mesh
                onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer';}}
                onPointerOut={(e) => { e.stopPropagation(); document.body.style.cursor = 'auto';}}
                onClick={(e) => { e.stopPropagation(); onSatelliteClick(project);}}
                scale={1}
                visible={satelliteIsRelevant || hovered}
              >
                <sphereGeometry args={[satelliteSize, 24, 24]} />
                <meshStandardMaterial 
                  color={project.color || '#888888'} 
                  metalness={0.3} 
                  roughness={0.7}
                  emissive={satelliteMatchesSkill ? '#FFFFFF' : '#555555'}
                  emissiveIntensity={satelliteMatchesSkill ? 0.4 : 0.1}
                  opacity={satelliteIsRelevant ? 1 : 0.6}
                  transparent={!satelliteIsRelevant}
                />
              </mesh>
              
              {/* 卫星标签 */}
              <Html distanceFactor={55} position={[0, satelliteSize * 2.5, 0]} center visible={satelliteIsRelevant || hovered}>
                <div className={`planet-label satellite-label ${satelliteIsRelevant ? '' : 'dimmed'}`}>
                  <h4>{project.title || project.name || '未命名项目'}</h4>
                </div>
              </Html>
            </group>
          );
        })}
      </group>
    </group>
  );
}